/**
 * 履歴一覧 (① / ②) のページング付き取得フック (マスタ変更履歴 SBI 7)。
 *
 * - `fetchPage(nextToken)` が 1 ページ分を返す。`resetKey` が変わると先頭から取り直す。
 * - 「さらに読み込む」は `next_token` を渡して続きを足す。
 * - 古いリクエストの結果で新しい結果を上書きしない (世代カウンタ。他の画面と同じ作法)。
 * - 失敗時は `error` に例外を入れる (文言への変換は画面側)。取得済みの行は残す。
 *
 * ①② で共有し、③ を別の PR に分けても影響しないよう、画面から切り離してある。
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { MasterHistoryListEntry, MasterHistoryListResponse } from '../api/types';

export interface MasterHistoryPages {
  items: MasterHistoryListEntry[];
  /** 先頭ページの取得中 */
  loading: boolean;
  /** 続きの取得中 */
  loadingMore: boolean;
  error: unknown;
  /** 続きがある (`next_token` が返っている) */
  hasMore: boolean;
  /** 先頭から取り直す */
  reload: () => void;
  loadMore: () => void;
}

export function useMasterHistoryPages(
  fetchPage: (nextToken: string | undefined) => Promise<MasterHistoryListResponse>,
  resetKey: string,
): MasterHistoryPages {
  const [items, setItems] = useState<MasterHistoryListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [reloadCount, setReloadCount] = useState(0);
  const requestIdRef = useRef(0);
  const fetchRef = useRef(fetchPage);

  useEffect(() => {
    fetchRef.current = fetchPage;
  }, [fetchPage]);

  useEffect(() => {
    // 条件の変更・アンマウントで世代を進め、進行中リクエストの結果を破棄する。
    const generation = requestIdRef;
    const requestId = ++generation.current;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- API 取得の初回ロードは正当な副作用 (条件変更時の取り直し)
    setItems([]);
    setNextToken(undefined);
    setError(null);
    setLoading(true);
    setLoadingMore(false);
    fetchRef
      .current(undefined)
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        setItems(res.items);
        setNextToken(res.next_token);
      })
      .catch((e: unknown) => {
        if (requestId !== requestIdRef.current) return;
        setError(e);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
    return () => {
      ++generation.current;
    };
  }, [resetKey, reloadCount]);

  const loadMore = useCallback((): void => {
    if (!nextToken) return;
    const requestId = ++requestIdRef.current;
    setLoadingMore(true);
    setError(null);
    fetchRef
      .current(nextToken)
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        setItems((prev) => [...prev, ...res.items]);
        setNextToken(res.next_token);
      })
      .catch((e: unknown) => {
        if (requestId !== requestIdRef.current) return;
        setError(e);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoadingMore(false);
      });
  }, [nextToken]);

  const reload = useCallback((): void => setReloadCount((n) => n + 1), []);

  return { items, loading, loadingMore, error, hasMore: nextToken !== undefined, reload, loadMore };
}
